import { useState, useContext, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UserContext } from '../../../Contexts/UserContext';

import { updateDoc, getDoc } from '../../../api/sites';
import ErrorBoundary from '../../../Components/CustomErrorBoundary/CustomErrorBoundary';
import PushDocModal from '../Components/PushDocModal';
import PageHeading from '../../../Components/PageHeading';

//mui imports====
import useStyles from '../styles';
import Typography from '@mui/material/Typography';
import { Box, Grid, Paper, Tab } from '@mui/material';
import { Button } from '@mui/material';
import { TabPanel } from '@mui/lab';
import TextField from '@mui/material/TextField';
import JsonEditor from '../../../Components/JsonEditor';
import GeneralTab from '../../../Components/GeneralTabAndTabContent';
import GamKeyUpdate from './GamKeyUpdate';
//===============
import { toast } from 'react-toastify';

const SuperAdmin = () => {
	const classes = useStyles('./styles');
	const { user } = useContext(UserContext);
	const [searchParams, setSearchParams] = useSearchParams();
	const requestedDocId = searchParams.get('docId') || '';

	const [docId, setDocId] = useState(requestedDocId);
	const [isPullingDoc, setIsPullingDoc] = useState(false);
	const [docObject, setDocObject] = useState({ json: {}, text: undefined }); // Json Editor needs an initialObject with json and text as props
	const [modalState, setModalState] = useState(false);
	const [previousCouchBaseConfig, setPreviousCouchBaseConfig] = useState();

	const resetDocState = useCallback(() => {
		setDocObject({ json: {}, text: undefined });
		setPreviousCouchBaseConfig(undefined);
	}, []);

	const getDocFromCouchBase = useCallback(async (targetDocId) => {
		if (targetDocId) {
			if (!user) {
				alert('No User found!');
				return;
			}
			if (!user.isSuperAdmin) {
				alert('User not allowed to pull doc!!');
				return;
			}

			resetDocState();
			setIsPullingDoc(true);
			try {
				const { data } = await getDoc(targetDocId);
				setDocObject({ json: data.data.doc, text: undefined });
				setPreviousCouchBaseConfig(data.data.doc);
			} catch (error) {
				toast.error(error?.response?.data?.data?.message || error?.message || 'Unable to pull document', {
					position: toast.POSITION.BOTTOM_RIGHT
				});
			} finally {
				setIsPullingDoc(false);
			}
		} else {
			alert('Please Enter Doc Id');
			return;
		}
	}, [resetDocState, user]);

	useEffect(() => {
		if (requestedDocId && user?.isSuperAdmin) {
			getDocFromCouchBase(requestedDocId);
		}
	}, [requestedDocId, user?.isSuperAdmin, getDocFromCouchBase]);

	const pushDocToCouchBase = async () => {
		let mode = 'UPDATE_SUPER_ADMIN';
		if (docId) {
			if (!user) {
				alert('No User found!');
				return;
			}
			if (!user.isSuperAdmin) {
				alert('User not allowed to pull doc!!');
				return;
			}

			try {
				let updatedObject = {}; // updatedObject will be based on whether user has updated text or json (tree format)
				if (docObject.text === undefined) {
					updatedObject = docObject.json; // updating updatedObject to be equal to Json if tree format is used
				} else if (docObject.json === undefined) {
					if (docObject.text === '') {
						updatedObject = {}; // updating updatedObject as empty object if the output of operation is empty or undefined
					} else {
						updatedObject = JSON.parse(docObject.text); //updating updatedObject as Json.parse object if text format is used
					}
				}
				const { data } = await updateDoc(docId, updatedObject, previousCouchBaseConfig, mode);

				// setting default values in state
				setDocObject({ json: {}, text: undefined });
				setPreviousCouchBaseConfig(null);
				setDocId('');
				const nextParams = new URLSearchParams(searchParams);
				nextParams.delete('docId');
				setSearchParams(nextParams, { replace: true });

				toast.success(data?.data?.message, {
					position: toast.POSITION.BOTTOM_RIGHT
				});
			} catch (error) {
				const errorMessage = error?.response?.data?.data?.message || error?.message;
				toast.error(errorMessage, {
					position: toast.POSITION.BOTTOM_RIGHT
				});
			}
		} else {
			alert('Please Enter Doc Id');
			return;
		}
	};

	const pushDocPopup = () => {
		// checking if docId and docObject are valid, either the docObject.json should be there or docObject.text must not undefined
		// using previousConfig value to validate if doc is pulled from Couchbase or not
		if (
			docId &&
			docObject &&
			((docObject?.json !== undefined &&
				(Object.keys(docObject.json).length !== 0 || previousCouchBaseConfig)) ||
				docObject?.text !== undefined)
		) {
			setModalState(true);
		} else {
			alert('Enter Doc Id and Pull Doc from CouchBase');
		}
	};

	const tabListAndPanels = [
		{
			tab: <Tab label="Doc Update" value="doc-update" />,
			tabPanel: (
				<TabPanel value="doc-update" sx={{ width: '100%', px: { xs: 1, md: 3 } }}>
					<Grid container spacing={2} className={classes.superAdminGrid}>
						<Grid item xs={3}>
							<Typography className={classes.heading} fontWeight={600} variant="h6">
								Doc ID
							</Typography>
							<TextField
								id="outlined-size-normal-side-id"
								style={{ width: '70%', margin: '1rem 0' }}
								value={docId}
								disabled={isPullingDoc}
								onChange={(e) => {
									setDocId(e.target.value);
								}}
								placeholder="Enter Doc ID here ..."
							/>
						</Grid>

						<Grid item xs={2}>
							<Button
								variant="contained"
								fontWeight={600}
								style={{ marginTop: '5rem' }}
								onClick={() => getDocFromCouchBase(docId)}
								disabled={isPullingDoc}
							>
								{isPullingDoc ? 'Pulling Doc…' : 'Pull Doc'}
							</Button>
						</Grid>

						<Grid item xs={2}>
							<Button
								variant="contained"
								fontWeight={600}
								style={{ marginTop: '5rem' }}
								onClick={pushDocPopup}
								disabled={isPullingDoc}
							>
								Push Doc
							</Button>
						</Grid>

						<PushDocModal
							docId={docId}
							modalState={modalState}
							setModalState={setModalState}
							pushDocToCouchBase={pushDocToCouchBase}
						/>
					</Grid>

					<Grid
						item
						xs={10}
						style={{
							border: '1px solid darkgrey',
							borderRadius: '4px',
							margin: '0 5rem 5rem 5rem',
							padding: '1rem'
						}}
					>
						<ErrorBoundary>
							<JsonEditor
								content={docObject}
								readOnly={isPullingDoc || !previousCouchBaseConfig}
								onChange={(val) => setDocObject(val)}
							/>
						</ErrorBoundary>
					</Grid>
				</TabPanel>
			)
		},
		{
			tab: <Tab label="GAM Key Update" value="gam-key-update" />,
			tabPanel: (
				<TabPanel value="gam-key-update" sx={{ width: '100%' }}>
					<GamKeyUpdate />
				</TabPanel>
			)
		}
	];

	return (
		<Paper className={classes.dashboardMainContent}>
			<PageHeading headingName="Super Admin" headingPath={['Dashboard', 'SuperAdmin']} />

			{!user?.isSuperAdmin ? (
				<Typography variant="h6" className={classes.heading} fontWeight="bold" marginTop="10rem">
					Contact Administrator for Super Admin Access
				</Typography>
			) : (
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
					<GeneralTab tabListAndPanels={tabListAndPanels} tabListStyles={{ marginTop: '2rem' }} />
				</Box>
			)}
		</Paper>
	);
};

export default SuperAdmin;
